/** 관리자 대시보드 공통 유틸 */

export const ADMIN_CATEGORY_OPTIONS = [
	{ value: '', text: '전체 카테고리' },
	{ value: 'SOIL', text: '토양' },
	{ value: 'WATER', text: '수질' },
	{ value: 'AIR', text: '대기' },
	{ value: 'DISPOSAL', text: '폐기물' }
]

export const ADMIN_DASHBOARD_MIN_WIDTH = 1400

export const CATEGORY_LABEL_TO_VALUE = {
	토양: 'SOIL',
	수질: 'WATER',
	대기: 'AIR',
	폐기물: 'DISPOSAL'
}

// ADMIN_ROUTE_PERMISSION(['ADMIN','SUB_ADMIN']) 은 어디서도 쓰이지 않아 제거했다
// (2026-08-17). 관리자 대시보드 접근은 권한 매트릭스의 'admin' 페이지 키가 정본이다
// — 라우터 가드(canAccessPage)와 사이드바(canShowMenu)가 같은 키로 판정한다.

/**
 * Date 또는 문자열을 YYYY-MM-DD 로 변환
 * @param {Date|string|null} d
 * @returns {string|null}
 */
export function toDateStr(d) {
	if (!d) return null
	const date = d instanceof Date ? d : new Date(d)
	if (isNaN(date.getTime())) return null
	const yyyy = date.getFullYear()
	const mm = String(date.getMonth() + 1).padStart(2, '0')
	const dd = String(date.getDate()).padStart(2, '0')
	return `${yyyy}-${mm}-${dd}`
}

/**
 * YYYY-MM-DD 문자열을 Date 로 변환
 * @param {string|null} str
 * @returns {Date|null}
 */
export function parseDateStr(str) {
	if (!str) return null
	const date = new Date(str)
	return isNaN(date.getTime()) ? null : date
}

/**
 * query 문자열에서 Date range 복원 [start, end]
 */
export function parseQueryDateRange(startKey, endKey, query = {}) {
	const start = parseDateStr(query[startKey])
	const end = parseDateStr(query[endKey])
	if (start && end) return [start, end]
	return null
}

/**
 * Date range 를 query 객체에 병합
 */
export function mergeQueryDateRange(query, startKey, endKey, range) {
	const next = { ...query }
	const start = toDateStr(range?.[0])
	const end = toDateStr(range?.[1])
	if (start) next[startKey] = start
	else delete next[startKey]
	if (end) next[endKey] = end
	else delete next[endKey]
	return next
}

/**
 * 최근 N일(포함 오늘) 구간 [시작일, 오늘]
 * @param {number} daysBack 기본 30
 * @returns {[Date, Date]}
 */
export function defaultRecentDaysRange(daysBack = 30) {
	const end = new Date()
	end.setHours(0, 0, 0, 0)
	const start = new Date(end)
	start.setDate(start.getDate() - (daysBack - 1))
	return [start, end]
}

/**
 * 국세청 전자세금계산서 조회화면 방식의 조회기간 선택 (2026-08-11 확인요청 문서).
 * 구분(연별/월별/분기별/반기별) + 연도 + 회차로 기간을 직접 고른다.
 * 종전 프리셋(오늘 기준 현재 기간만)과 달리 과거 기간도 선택 가능하다.
 */
export const PERIOD_UNIT_OPTIONS = [
	{ value: 'year', text: '연별' },
	{ value: 'month', text: '월별' },
	{ value: 'quarter', text: '분기별' },
	{ value: 'half', text: '반기별' }
]

/** 구분별 회차 드롭다운 옵션 */
export function getPeriodIndexOptions(unit) {
	if (unit === 'month') {
		return Array.from({ length: 12 }, (_, i) => ({ value: i + 1, text: `${String(i + 1).padStart(2, '0')}월` }))
	}
	if (unit === 'quarter') {
		return [1, 2, 3, 4].map(q => ({ value: q, text: `${q}분기` }))
	}
	if (unit === 'half') {
		return [1, 2].map(h => ({ value: h, text: `${h}기` }))
	}
	return []
}

/**
 * 선택(구분+연도+회차) → [시작일, 종료일]
 * @param {{unit:'year'|'month'|'quarter'|'half', year:number, index?:number}} sel
 * @returns {[Date, Date]|null}
 */
export function getPeriodRangeBySelection(sel) {
	if (!sel || !sel.unit || !Number.isInteger(sel.year)) return null
	const y = sel.year
	const i = sel.index
	switch (sel.unit) {
		case 'year':
			return [new Date(y, 0, 1), new Date(y, 11, 31)]
		case 'month':
			if (!Number.isInteger(i) || i < 1 || i > 12) return null
			return [new Date(y, i - 1, 1), new Date(y, i, 0)]
		case 'quarter':
			if (!Number.isInteger(i) || i < 1 || i > 4) return null
			return [new Date(y, (i - 1) * 3, 1), new Date(y, i * 3, 0)]
		case 'half':
			if (!Number.isInteger(i) || i < 1 || i > 2) return null
			return [new Date(y, (i - 1) * 6, 1), new Date(y, i * 6, 0)]
		default:
			return null
	}
}

/** 오늘 기준 기본 선택값 — 구분만 고르면 현재 기간이 미리 선택되게 */
export function getDefaultPeriodSelection(unit, refDate = new Date()) {
	const y = refDate.getFullYear()
	const m = refDate.getMonth()
	if (unit === 'year') return { unit, year: y }
	if (unit === 'month') return { unit, year: y, index: m + 1 }
	if (unit === 'quarter') return { unit, year: y, index: Math.floor(m / 3) + 1 }
	if (unit === 'half') return { unit, year: y, index: m < 6 ? 1 : 2 }
	return null
}

/** URL 쿼리 직렬화 — 'month:2026:8' 형태. 구버전 단순값('month')은 오늘 기준으로 해석 */
export function encodePeriodSelection(sel) {
	if (!sel || !sel.unit) return ''
	return sel.unit === 'year' ? `${sel.unit}:${sel.year}` : `${sel.unit}:${sel.year}:${sel.index}`
}

export function decodePeriodSelection(str, refDate = new Date()) {
	if (!str) return null
	const parts = String(str).split(':')
	const unit = parts[0]
	if (!PERIOD_UNIT_OPTIONS.some(o => o.value === unit)) return null
	if (parts.length === 1) return getDefaultPeriodSelection(unit, refDate) // 구버전 호환
	const year = Number(parts[1])
	if (!Number.isInteger(year)) return null
	if (unit === 'year') return { unit, year }
	const index = Number(parts[2])
	const sel = { unit, year, index }
	return getPeriodRangeBySelection(sel) ? sel : null
}

/** 업무 현황 기간 프리셋 */
export const PERIOD_PRESET_OPTIONS = [
	{ value: 'year', text: '연도' },
	{ value: 'month', text: '월' },
	{ value: 'quarter', text: '분기' },
	{ value: 'half', text: '반기' }
]

const PERIOD_PRESET_VALUES = PERIOD_PRESET_OPTIONS.map(o => o.value)

/**
 * 기간 프리셋 → [시작일, 종료일]
 * @param {'year'|'month'|'quarter'|'half'} type
 * @param {Date} [refDate] 기준일 (기본: 오늘)
 * @returns {[Date, Date]|null}
 */
export function getPeriodDateRange(type, refDate = new Date()) {
	if (!PERIOD_PRESET_VALUES.includes(type)) return null

	const ref = new Date(refDate)
	ref.setHours(0, 0, 0, 0)
	const y = ref.getFullYear()
	const m = ref.getMonth()
	let start
	let end

	switch (type) {
		case 'year':
			start = new Date(y, 0, 1)
			end = new Date(y, 11, 31)
			break
		case 'month':
			start = new Date(y, m, 1)
			end = new Date(y, m + 1, 0)
			break
		case 'quarter': {
			const qStart = Math.floor(m / 3) * 3
			start = new Date(y, qStart, 1)
			end = new Date(y, qStart + 3, 0)
			break
		}
		case 'half': {
			const hStart = m < 6 ? 0 : 6
			start = new Date(y, hStart, 1)
			end = new Date(y, hStart + 6, 0)
			break
		}
		default:
			return null
	}

	const today = new Date()
	today.setHours(0, 0, 0, 0)
	if (end > today) end = today

	return [start, end]
}

/** 기간 프리셋 → 매출 summary/series viewMode */
export function getPeriodViewMode(preset) {
	switch (preset) {
		case 'year': return 'YEARLY'
		case 'quarter': return 'QUARTERLY'
		case 'half': return 'MONTHLY'
		case 'month':
		default: return 'MONTHLY'
	}
}

/**
 * 지정 연도·월의 [시작일, 말일] Date range 반환
 * @param {number} year 예: 2026
 * @param {number} month 1~12
 * @returns {[Date, Date]}
 */
export function monthDateRange(year, month) {
	const start = new Date(year, month - 1, 1)
	start.setHours(0, 0, 0, 0)
	const end = new Date(year, month, 0)
	end.setHours(0, 0, 0, 0)
	return [start, end]
}

/**
 * 오늘(또는 referenceDate) 기준 해당 월 range.
 * 선택 월이 아직 오지 않은 미래 월이면 전년도 같은 월을 사용한다.
 * @param {number} month 1~12
 * @param {Date} [referenceDate=new Date()]
 * @returns {[Date, Date]}
 */
export function currentYearMonthRange(month, referenceDate = new Date()) {
	const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate)
	const year = ref.getFullYear()
	const currentMonth = ref.getMonth() + 1
	const targetYear = month > currentMonth ? year - 1 : year
	return monthDateRange(targetYear, month)
}

/**
 * 차트 segment 클릭 → 시험 의뢰 목록 드릴다운
 * @param {object} opts
 * @param {string} [opts.status]                  - 업무 상태 (WORK_ACCEPTED 등)
 * @param {string|string[]} [opts.category]        - 업무 코드 접두어 (EA, EB …)
 * @param {string} [opts.researchDeptKeyword]      - 시험부서 부분 검색어 (토양/수질/대기/폐기물)
 *                                                   list.vue에서 filterList 로드 후 실제 analysis_dept_id 값으로 해소됨
 * @param {string} [opts.startDatetime]            - 의뢰일 시작 (YYYY-MM-DD)
 * @param {string} [opts.endDatetime]              - 의뢰일 종료 (YYYY-MM-DD)
 */
export function navigateToProjectList(router, store, { status, category, researchDeptKeyword, startDatetime, endDatetime } = {}) {
	const filter = {}
	if (status) filter.status = status
	if (category) {
		filter.category = Array.isArray(category) ? category : [category]
	}
	if (researchDeptKeyword) {
		// list.vue의 loadFilterList() 완료 후 실제 analysis_dept_id 값으로 해소
		filter._researchDeptKeyword = researchDeptKeyword
	}
	if (startDatetime && endDatetime) {
		filter.startDatetime = [startDatetime, endDatetime]
	}
	store.commit('project/list/setParams', {
		page: 1,
		filter
	})
	router.push({ path: '/project/list', query: { drilldown: '1' } })
}

/**
 * doughnut / bar 차트용 onClick 옵션 생성
 */
export function buildChartClickOptions(handler) {
	return {
		onClick: (_event, elements, chart) => {
			if (!elements?.length || typeof handler !== 'function') return
			const el = elements[0]
			const datasetIndex = el.datasetIndex
			const index = el.index
			const label = chart.data.labels?.[index]
			const datasetLabel = chart.data.datasets?.[datasetIndex]?.label
			handler({ label, index, datasetIndex, datasetLabel })
		}
	}
}

